// libraries
import type { DealerInstalledOptions } from '@makemydeal/dr-dash-bff-types';
import { createSelector } from 'reselect';

// state
import type { StateTree } from '@makemydeal/dr-dash-types';
import type { DcVppVehicleUsage, IVehicle, OfferType } from '@makemydeal/dr-platform-types';
import { CASH } from '@makemydeal/dr-platform-types';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

export const getVehicle = (state: StateTree): any => {
    return state.vehicle || {};
};

export const getVehicleCondition = (state: StateTree): string => {
    return getVehicle(state).condition;
};

export const getVehicleConditionOrDefault = (state: StateTree): string => {
    return getVehicle(state).condition || '';
};

export const showVehicleChangeSnackBar = (state: StateTree): boolean => {
    return getVehicle(state).showVehicleChangeSnackBar || false;
};

export const getCashFallbackFromOfferType = (state: StateTree): OfferType | undefined =>
    state.offer?.offerType === CASH ? getVehicle(state).cashFallbackFromOfferType : undefined;

export const showEditVehicle = (state: StateTree): boolean => {
    return getVehicle(state).showEditVehicle || false;
};

export const getVehicleConditionLower = (state: StateTree): string => {
    return getVehicleConditionOrDefault(state).toLowerCase();
};

export const getVehicleImageUrl = (state: StateTree): string => {
    return getVehicle(state).imageUrl;
};

export const getVehicleModel = (state: StateTree): string => {
    return getVehicle(state).model;
};

export const getVehicleMake = (state: StateTree): string => {
    return getVehicle(state).make;
};

export const getVehicleTrim = (state: StateTree): string => {
    return getVehicle(state).trim;
};

export const getHasVehicle = (state: StateTree): boolean => {
    return Boolean(getVehicleMake(state) || getVehicleModel(state) || getVehicleTrim(state));
};

export const getVehicleVin = (state: StateTree): string => {
    return getVehicle(state).vin;
};

export const getVehicleExteriorColor = (state: StateTree): string => {
    return getVehicle(state).exteriorColor;
};

export const getVehicleInteriorColor = (state: StateTree): string => {
    return getVehicle(state).interiorColor;
};

export const getStockNumber = (state: StateTree): string => {
    return getVehicle(state).stockNumber;
};

export const getBodyStyle = (state: StateTree): string => {
    return getVehicle(state).bodyStyle;
};

export const getDriveLine = (state: StateTree): string => {
    return getVehicle(state).driveLine;
};

export const getVehicleYear = (state: StateTree): number => {
    return getVehicle(state).year;
};

export const getListedPrice = (state: StateTree): number => {
    return getVehicle(state).listedPrice;
};

export const getRetailPrice = (state: StateTree): number => {
    return getVehicle(state).retailPrice;
};

export const getVehicleStockNumber = (state: StateTree) => {
    return getVehicle(state).stockNumber;
};

export const getVehicleId = (state: StateTree) => {
    return getVehicle(state).vehicleId;
};

export const getVehicleEngine = (state: StateTree): string => {
    return getVehicle(state).engine;
};

export const getVehicleSearchParam = (state: StateTree) => {
    return { search: getVehicleVin(state) };
};

export const getInitialPageSize = () => {
    return 10;
};

export const getVehicleEngineSize = (state: StateTree): string => {
    const vehicleDetails: IVehicle = getVehicle(state);
    const engineSize = vehicleDetails.engineSize ? `${vehicleDetails.engineSize}` : '';
    const engine = vehicleDetails.engine ? vehicleDetails.engine : '';
    return engineSize && engine ? `${engineSize} ${engine}` : engineSize || engine;
};

export function getDealerInstalledOptions(state: StateTree) {
    const vehicle = getVehicle(state);
    return vehicle.dealerInstalledOptions || [];
}

export function getDIOTotalValues(state: StateTree): number {
    const installedOptions = getDealerInstalledOptions(state) as DealerInstalledOptions[];
    if (installedOptions.length <= 0) return 0;
    const totalDIOValues = installedOptions.reduce((accumulator, currentValue) => accumulator + currentValue.value, 0);
    return totalDIOValues;
}
export const isVehicleNew = (state: StateTree) => {
    return getVehicleConditionLower(state) === 'new';
};

export const getMileage = (state: StateTree) => {
    return getVehicle(state).mileage;
};

export const getFuel = (state: StateTree): string => {
    return getVehicle(state).fuel;
};

export const getVehicleCylinders = (state: StateTree): number | undefined => {
    return getVehicle(state).cylinders;
};

export const getVehicleOdometerMileageType = (state: StateTree): boolean | undefined => {
    return getVehicle(state).odometerMileageType;
};

export const getInvoiceAmount = (state: StateTree): number | undefined => {
    return getVehicle(state).invoiceAmount;
};

export const getCviCost = (state: StateTree): number | undefined => {
    return getVehicle(state).cviCost;
};

export const getVehicleCost = (state: StateTree): number | undefined => {
    return getVehicle(state).cost;
};

export const getDealCostAtCviFetch = (state: StateTree): number | undefined => {
    return getVehicle(state).dealCostAtCviFetch;
};

export const getCviInvoiceAmount = (state: StateTree): number | undefined => {
    return getVehicle(state).cviInvoiceAmount;
};

export const getDealInvoiceAtCviFetch = (state: StateTree): number | undefined => {
    return getVehicle(state).dealInvoiceAtCviFetch;
};

const toCents = (value: number): number => Math.round(value * 100);

const hasCviValueMismatch = (
    current: number | undefined,
    cviValue: number | undefined,
    dealValueAtFetch: number | undefined
): boolean => {
    const hasValidValues = dealValueAtFetch != null && dealValueAtFetch > 0 && cviValue != null && cviValue > 0;
    if (!hasValidValues) {
        return false;
    }

    const cviValueDifferedAtFetch = toCents(dealValueAtFetch) !== toCents(cviValue);
    if (!cviValueDifferedAtFetch) {
        return false;
    }

    const currentValueDoesNotMatchCvi = current == null || toCents(current) !== toCents(cviValue);
    return currentValueDoesNotMatchCvi;
};

export const hasCviCostMismatch = (state: StateTree): boolean =>
    hasCviValueMismatch(getVehicleCost(state), getCviCost(state), getDealCostAtCviFetch(state));

export const hasCviInvoiceMismatch = (state: StateTree): boolean =>
    hasCviValueMismatch(getInvoiceAmount(state), getCviInvoiceAmount(state), getDealInvoiceAtCviFetch(state)) &&
    featureToggleSelectors.isCviInvoiceMismatchAlertEnabled(state);

export const formatFuelEconomyValue = (
    cityFuelEconomy: string | undefined,
    highwayFuelEconomy: string | undefined,
    fuel: string
): string => {
    let fuelEconomyValue: string;

    if (!cityFuelEconomy && !highwayFuelEconomy) {
        fuelEconomyValue = fuel;
    } else if (cityFuelEconomy && highwayFuelEconomy) {
        fuelEconomyValue = `${cityFuelEconomy} / ${highwayFuelEconomy}`;
    } else {
        fuelEconomyValue = (cityFuelEconomy || highwayFuelEconomy) as string;
    }

    return fuelEconomyValue;
};

export const getFuelEconomyValue = (state: StateTree): string => {
    const vehicleDetails: IVehicle = getVehicle(state);
    const fuel = getFuel(state);

    return formatFuelEconomyValue(
        vehicleDetails.cityFuelEconomy ? `${vehicleDetails.cityFuelEconomy.toFixed(1)}` : '',
        vehicleDetails.highwayFuelEconomy ? `${vehicleDetails.highwayFuelEconomy.toFixed(1)}` : '',
        fuel
    );
};

/**
 * Gets vehicle details formatted for CDL analytics payload.
 * Used by Desking Intelligence analytics tags (dc-mv-260, 261, 278-282).
 */
export const getVehicleAnalyticsPayload = createSelector([getVehicle], (vehicle) => ({
    make: vehicle.make,
    model: vehicle.model,
    year: vehicle.year,
    vin: vehicle.vin
}));

export const getIsQuoteWithoutVehicle = (state: StateTree): boolean => {
    const isQuoteMode = featureToggleSelectors.isManagerViewQuoteModeEnabled(state);
    const vehicleExists = getHasVehicle(state);
    return isQuoteMode && !vehicleExists;
};

// Sole owner of vehicleUse: a surcharge fallback here would only resurrect a cleared value.
export const getVehicleUse = (state: StateTree): DcVppVehicleUsage | undefined => {
    return getVehicle(state).vehicleUse;
};

export const getPrincipalDriverName = (state: StateTree): string | undefined => {
    return getVehicle(state).principalDriverName;
};
